import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from "@mui/icons-material";
import { patientService, PatientOrder } from "../../services/patient.service";
import { toLocaleDateString } from "../../utils/dateUtils";

interface Props {
  patientId: number;
}

interface OrderRowProps {
  order: PatientOrder;
}

function OrderRow({ order }: OrderRowProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton size='small' onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{toLocaleDateString(order.poDate)}</TableCell>
        <TableCell>{order.fromCompanyName}</TableCell>
        <TableCell>{order.poNumber}</TableCell>
        <TableCell>{order.clientCompanyName}</TableCell>
        <TableCell align='right'>{order.details?.length || 0}</TableCell>
        <TableCell align='right'>${order.total?.toFixed(2)}</TableCell>
        <TableCell>{order.status}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant='h6' gutterBottom component='div'>
                Order Details
              </Typography>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align='right'>Unit Price</TableCell>
                    <TableCell align='right'>Quantity</TableCell>
                    <TableCell align='right'>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.details?.map((detail) => (
                    <TableRow key={detail.id}>
                      <TableCell>{detail.chargeName}</TableCell>
                      <TableCell align='right'>
                        ${detail.charge?.toFixed(2)}
                      </TableCell>
                      <TableCell align='right'>{detail.qty}</TableCell>
                      <TableCell align='right'>
                        ${detail.total?.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const PatientOrdersTab: React.FC<Props> = ({ patientId }) => {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<PatientOrder[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    loadOrders();
  }, [patientId]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await patientService.getPatientOrders(patientId);
      setOrders(data);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity='error'>{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant='h6' gutterBottom>
        Patient Orders
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>PO Date</TableCell>
              <TableCell>From</TableCell>
              <TableCell>PO Number</TableCell>
              <TableCell>Client Name</TableCell>
              <TableCell align='right'>Items</TableCell>
              <TableCell align='right'>Total</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <OrderRow key={order.id} order={order} />
            ))}
            {orders.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align='center'>
                  No orders found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PatientOrdersTab;
